import React from 'react';
import { Modal, Button, Form, Alert, Spinner, Badge } from 'react-bootstrap';
import { CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { useConfigImport } from '../../hooks/setup/useConfigImport';

interface ConfigImportModalProps {
    show: boolean;
    /** didImport = at least one section was written; caller should refresh */
    onClose: (didImport: boolean) => void;
}

/**
 * Uploads a configuration file exported from another instance
 * (Superadmin → Exportera konfiguration) and imports selected sections.
 * Import REPLACES each selected section in this instance.
 */
const ConfigImportModal: React.FC<ConfigImportModalProps> = ({ show, onClose }) => {
    const {
        phase,
        bundle,
        previews,
        selected,
        toggleSection,
        results,
        activeSection,
        error,
        selectFile,
        runImport,
        reset,
        didImport,
    } = useConfigImport();

    const handleClose = () => {
        if (phase === 'importing') return;
        onClose(didImport);
        reset();
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) selectFile(file);
        e.target.value = '';
    };

    const selectedPreviews = previews.filter(p => selected.has(p.key) && p.errors.length === 0);
    const willOverwrite = selectedPreviews.some(p => (p.existingCount ?? 0) > 0);

    return (
        <Modal show={show} onHide={handleClose} size="lg" backdrop="static">
            <Modal.Header closeButton={phase !== 'importing'}>
                <Modal.Title>Importera konfiguration</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {phase === 'idle' && (
                    <>
                        <p className="text-muted">
                            Ladda upp en konfigurationsfil som exporterats från en annan instans
                            via Superadmin → Exportera konfiguration.
                        </p>
                        {error && <Alert variant="danger">{error}</Alert>}
                        <Form.Group>
                            <Form.Label>Konfigurationsfil (JSON)</Form.Label>
                            <Form.Control
                                type="file"
                                accept="application/json,.json"
                                onChange={handleFileChange}
                            />
                        </Form.Group>
                    </>
                )}

                {phase === 'loadingPreview' && (
                    <div className="text-center py-4">
                        <Spinner animation="border" />
                        <p className="text-muted mt-2 mb-0">Läser filen...</p>
                    </div>
                )}

                {(phase === 'preview' || phase === 'importing' || phase === 'done') && bundle && (
                    <>
                        <p className="mb-3">
                            Exporterad från{' '}
                            <strong>
                                {bundle.exportedFrom.instanceName || bundle.exportedFrom.projectId || 'okänd instans'}
                            </strong>{' '}
                            <span className="text-muted">({bundle.exportedAt})</span>
                        </p>

                        {phase === 'preview' && willOverwrite && (
                            <Alert variant="warning" className="d-flex align-items-center gap-2">
                                <AlertTriangle size={18} className="flex-shrink-0" />
                                <span>
                                    Markerade sektioner med befintlig data <strong>ersätts helt</strong> av
                                    innehållet i filen. Detta går inte att ångra.
                                </span>
                            </Alert>
                        )}

                        <div className="d-flex flex-column gap-2">
                            {previews.map(preview => {
                                const result = results[preview.key];
                                const importable = preview.errors.length === 0;
                                return (
                                    <div key={preview.key} className="border rounded p-2 px-3">
                                        <div className="d-flex align-items-center gap-2">
                                            {phase === 'preview' ? (
                                                <Form.Check
                                                    type="checkbox"
                                                    id={`import-section-${preview.key}`}
                                                    checked={selected.has(preview.key)}
                                                    disabled={!importable}
                                                    onChange={() => toggleSection(preview.key)}
                                                />
                                            ) : activeSection === preview.key ? (
                                                <Spinner animation="border" size="sm" />
                                            ) : result?.status === 'success' ? (
                                                <CheckCircle size={18} color="#198754" />
                                            ) : result?.status === 'error' ? (
                                                <XCircle size={18} color="#dc3545" />
                                            ) : (
                                                <span style={{ width: 18 }} />
                                            )}
                                            <div className="flex-grow-1">
                                                <strong>{preview.label}</strong>{' '}
                                                <span className="text-muted">{preview.entryCount} poster</span>
                                            </div>
                                            {phase === 'preview' && importable && (preview.existingCount ?? 0) > 0 && (
                                                <Badge bg="warning" text="dark">
                                                    Ersätter {preview.existingCount} befintliga
                                                </Badge>
                                            )}
                                        </div>
                                        {result && (
                                            <div className={`small ms-4 ${result.status === 'error' ? 'text-danger' : 'text-muted'}`}>
                                                {result.message}
                                            </div>
                                        )}
                                        {preview.errors.map((sectionError, i) => (
                                            <div key={i} className="small text-danger ms-4">{sectionError}</div>
                                        ))}
                                        {preview.warnings.map((warning, i) => (
                                            <div key={i} className="small text-muted ms-4">{warning}</div>
                                        ))}
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                {phase === 'preview' && (
                    <>
                        <Button variant="outline-secondary" onClick={handleClose}>
                            Avbryt
                        </Button>
                        <Button
                            variant={willOverwrite ? 'danger' : 'primary'}
                            disabled={selectedPreviews.length === 0}
                            onClick={runImport}
                        >
                            Importera {selectedPreviews.length}{' '}
                            {selectedPreviews.length === 1 ? 'sektion' : 'sektioner'}
                        </Button>
                    </>
                )}
                {phase === 'importing' && (
                    <Button variant="primary" disabled>
                        <Spinner size="sm" className="me-2" />
                        Importerar...
                    </Button>
                )}
                {phase === 'done' && (
                    <Button variant="primary" onClick={handleClose}>
                        Stäng
                    </Button>
                )}
                {phase === 'idle' && (
                    <Button variant="outline-secondary" onClick={handleClose}>
                        Avbryt
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
    );
};

export default ConfigImportModal;
